import { LATE_MINUTES, laneOf, minutesSince } from './kitchen';
import type { Order } from './types';

/** What a table is doing right now: shared by the tile view and the 3D view. */
export type TableState = 'free' | 'seated' | 'cooking' | 'eating' | 'bill' | 'late';

export const STATE_LABEL: Record<TableState, string> = {
  free: 'ว่าง · Free', seated: 'นั่งแล้ว · Seated', cooking: 'รออาหาร · Cooking',
  eating: 'รอเสิร์ฟ · To serve', bill: 'เช็คบิล · Bill', late: 'ช้า · Late',
};
/** Marker colours in 3D (also distinct in lightness, not hue alone). */
export const STATE_COLOR: Record<TableState, string> = {
  free: '#9AA1B8', seated: '#4F86F0', cooking: '#FFB21E', eating: '#2FB36B', bill: '#7C5CFF', late: '#E5484D',
};

export interface SessionLike { id: string; guests?: number; openedAt: string; billRequestedAt?: string }

export interface TableSummary {
  state: TableState;
  guests: number;
  minutes: number;
  orderIds: string[];
  dishes: number;
  ready: number;
  served: number;
  billRequested: boolean;
}

export function summarize(session: SessionLike | null | undefined, orders: Order[], now: number): TableSummary {
  if (!session) return { state: 'free', guests: 0, minutes: 0, orderIds: [], dishes: 0, ready: 0, served: 0, billRequested: false };
  const lines = orders.flatMap((o) => o.lines.filter((l) => l.status !== 'cancelled'));
  const cooking = orders.filter((o) => { const lane = laneOf(o); return lane === 'new' || lane === 'cooking'; });
  const late = cooking.some((o) => minutesSince(o.placedAt, now) >= LATE_MINUTES);
  const billRequested = !!session.billRequestedAt;
  const state: TableState = late ? 'late' : billRequested ? 'bill' : cooking.length ? 'cooking' : orders.length ? 'eating' : 'seated';
  return {
    state, guests: session.guests ?? 0, minutes: minutesSince(session.openedAt, now),
    orderIds: orders.map((o) => o.id), dishes: lines.reduce((n, l) => n + l.qty, 0),
    ready: lines.filter((l) => l.status === 'ready').reduce((n, l) => n + l.qty, 0),
    served: lines.filter((l) => l.status === 'served').reduce((n, l) => n + l.qty, 0),
    billRequested,
  };
}

export type FloatKind = 'seated' | 'order' | 'ready' | 'bill' | 'paid';
export interface FloatUp { kind: FloatKind; text: string }

/** Things that just happened at a table, to float up from it in 3D. Nothing on first sight (prev undefined). */
export function happenings(prev: TableSummary | undefined, next: TableSummary, orders: Order[]): FloatUp[] {
  if (!prev) return [];
  const out: FloatUp[] = [];
  if (prev.state === 'free' && next.state !== 'free') out.push({ kind: 'seated', text: next.guests ? `นั่งแล้ว ${next.guests} ท่าน · Seated` : 'นั่งแล้ว · Seated' });
  if (prev.state !== 'free' && next.state === 'free') out.push({ kind: 'paid', text: 'ปิดโต๊ะ · Table cleared' });
  const fresh = orders.filter((o) => !prev.orderIds.includes(o.id));
  const freshDishes = fresh.reduce((n, o) => n + o.lines.reduce((m, l) => m + l.qty, 0), 0);
  if (fresh.length && next.state !== 'free') out.push({ kind: 'order', text: `+${freshDishes} สั่งใหม่ · New order` });
  if (next.ready > prev.ready) out.push({ kind: 'ready', text: `${next.ready} พร้อมเสิร์ฟ · Ready` });
  if (next.billRequested && !prev.billRequested) out.push({ kind: 'bill', text: 'ขอเช็คบิล · Bill please' });
  return out;
}

/** Tables that want a person now: drives the per-room badges. */
export const needsAttention = (s: TableSummary) => s.state === 'bill' || s.state === 'late' || s.ready > 0;
